/**
 * Product Analytics Calculations
 * Replicates Cloud Function logic for client-side calculation
 * Used when rep filter is active to show rep-specific product analytics
 */

/**
 * Helper: Check if an item should be excluded from analytics
 * Skips:
 *   - Misc charges (items without a size field) — embroidery/freight/etc.
 *   - "O/S" (one-size) items — emblems, digitizing files, add-on services.
 *     ApparelMagic's Order Analysis report excludes these from Units totals,
 *     so we match that behavior for revenue AND unit counts across the CRM.
 * @param {Object} item - Order item
 * @returns {boolean} True if item should be skipped
 */
function shouldSkipItem(item) {
  const size = item.size || item.size_name || item.sizeName || item.Size;
  if (!size) return true; // Misc charges
  const s = String(size).trim().toUpperCase();
  if (s === 'O/S' || s === 'O / S' || s === 'ONE SIZE') return true;
  return false;
}

/**
 * Calculate Total Item Revenue from all order line items
 * This matches the product analytics tables (topStyles, topSizes, etc.)
 * and should be used for the "Total Item Revenue" card display.
 * 
 * @param {Array} orders - Orders with order_items array
 * @returns {number} Total revenue from items with non-empty size
 */
export function calculateTotalItemRevenue(orders) {
  let totalItemRevenue = 0;
  
  orders.forEach(order => {
    if (!order.order_items || !Array.isArray(order.order_items)) return;
    
    order.order_items.forEach(item => {
      // Skip items with empty or null size to match product analytics exclusion
      if (shouldSkipItem(item)) return;
      
      // Use item.amount (line item total) to match other calculations
      totalItemRevenue += parseFloat(item.amount || 0);
    });
  });
  
  return totalItemRevenue;
}

/**
 * Calculate Total Items (units) from all order line items
 * Sums item.qty across all orders, excluding misc charges (items without a size field).
 *
 * @param {Array} orders - Orders with order_items array
 * @returns {number} Total quantity of items ordered
 */
export function calculateTotalItems(orders) {
  let totalItems = 0;

  orders.forEach(order => {
    if (!order.order_items || !Array.isArray(order.order_items)) return;

    order.order_items.forEach(item => {
      // Skip misc charges (no size field) to match revenue calculation exclusions
      if (shouldSkipItem(item)) return;
      totalItems += parseFloat(item.qty) || 0;
    });
  });

  return totalItems;
}

/**
 * Calculate total revenue including misc charges across all orders
 * Items revenue + misc_charges (embroidery, freight, etc.)
 * @param {Array} orders - Orders with order_items and misc_charges arrays
 * @returns {number} Total revenue including misc charges
 */
export function calculateTotalRevenueWithMisc(orders) {
  let total = 0;
  orders.forEach(order => {
    total += getOrderTotalRevenue(order);
  });
  return total;
}

/**
 * Helper: Calculate total revenue for an order (items + misc charges)
 * @param {Object} order - Order object
 * @returns {number} Total revenue including misc charges
 */
function getOrderTotalRevenue(order) {
  // Sum order_items revenue
  const itemsRevenue = (order.order_items || []).reduce((sum, item) => {
    return sum + parseFloat(item.amount || 0);
  }, 0);
  
  // Sum misc_charges revenue
  const miscRevenue = (order.misc_charges || []).reduce((sum, charge) => {
    return sum + parseFloat(charge.amount || 0);
  }, 0);
  
  return itemsRevenue + miscRevenue;
}

/**
 * Calculate Top 10 Styles by Revenue
 * @param {Array} orders - Orders with order_items array
 * @returns {Array} Top 10 styles sorted by revenue
 */
export function calculateTopStyles(orders) {
  const styleMap = new Map();
  let totalRevenue = 0;

  orders.forEach(order => {
    const items = order.order_items || [];
    items.forEach(item => {
      // Skip items with empty or null size to match product analytics exclusion
      if (shouldSkipItem(item)) {
        return; // Skip this item
      }
      
      // Style = style_number only (e.g., "2458", "9010")
      const styleNumber = item.style_number || 'Unknown';

      // Use amount (line item total) to match order totals
      const revenue = parseFloat(item.amount || 0);
      const quantity = parseInt(item.qty || item.quantity || 0, 10);

      if (!styleMap.has(styleNumber)) {
        styleMap.set(styleNumber, {
          style: styleNumber,
          revenue: 0,
          quantity: 0,
          orderCount: 0,
          orderIds: new Set()
        });
      }

      const styleData = styleMap.get(styleNumber);
      styleData.revenue += revenue;
      styleData.quantity += quantity;
      
      // Add to total revenue (items only, no misc_charges)
      totalRevenue += revenue;
      
      if (!styleData.orderIds.has(order.id)) {
        styleData.orderCount++;
        styleData.orderIds.add(order.id);
      }
    });
  });

  // Convert to array and calculate percentages
  const styles = Array.from(styleMap.values()).map(style => ({
    style: style.style,
    revenue: style.revenue,
    quantity: style.quantity,
    orderCount: style.orderCount,
    percentage: totalRevenue > 0 ? (style.revenue / totalRevenue) * 100 : 0
  }));

  // Sort by revenue descending and return top 10
  return styles.sort((a, b) => b.revenue - a.revenue).slice(0, 10);
}

/**
 * Calculate Top 10 Collections by Revenue
 * @param {Array} orders - Orders with order_items array
 * @returns {Array} Top 10 collections sorted by revenue
 */
export function calculateTopCollections(orders) {
  const collectionMap = new Map();
  let totalRevenue = 0;

  orders.forEach(order => {
    const items = order.order_items || [];
    items.forEach(item => {
      // Skip items with empty or null size
      if (shouldSkipItem(item)) {
        return; // Skip this item
      }
      
      // Collection is in attr_3 field
      const collectionName = item.attr_3 || 'Unknown';
      // Use amount (line item total) to match order totals
      const revenue = parseFloat(item.amount || 0);
      const quantity = parseInt(item.qty || item.quantity || 0, 10);

      if (!collectionMap.has(collectionName)) {
        collectionMap.set(collectionName, {
          collection: collectionName,
          revenue: 0,
          quantity: 0,
          orderCount: 0,
          orderIds: new Set()
        });
      }

      const collectionData = collectionMap.get(collectionName);
      collectionData.revenue += revenue;
      collectionData.quantity += quantity;
      
      // Add to total revenue (items only, no misc_charges)
      totalRevenue += revenue;
      
      if (!collectionData.orderIds.has(order.id)) {
        collectionData.orderCount++;
        collectionData.orderIds.add(order.id);
      }
    });
  });

  // Convert to array and calculate percentages
  const collections = Array.from(collectionMap.values()).map(c => ({
    collection: c.collection,
    revenue: c.revenue,
    quantity: c.quantity,
    orderCount: c.orderCount,
    percentage: totalRevenue > 0 ? (c.revenue / totalRevenue) * 100 : 0
  }));

  // Sort by revenue descending and return top 10
  return collections.sort((a, b) => b.revenue - a.revenue).slice(0, 10);
}

/**
 * Calculate Top 10 Sizes by Revenue
 * @param {Array} orders - Orders with order_items array
 * @returns {Array} Top 10 sizes sorted by revenue
 */
export function calculateTopSizes(orders) {
  const sizeMap = new Map();
  let totalRevenue = 0;

  orders.forEach(order => {
    const items = order.order_items || [];
    items.forEach(item => {
      // Skip items with empty or null size
      if (shouldSkipItem(item)) {
        return; // Skip this item - DO NOT add to totalRevenue
      }
      
      // Try multiple possible size field names
      const size = item.size || item.size_name || item.sizeName || item.Size || 'Unknown';
      
      // Use amount (line item total) to match order totals
      const revenue = parseFloat(item.amount || 0);
      const quantity = parseInt(item.quantity || item.qty || 0, 10);

      if (!sizeMap.has(size)) {
        sizeMap.set(size, {
          size: size,
          revenue: 0,
          quantity: 0,
          orderCount: 0,
          orderIds: new Set()
        });
      }

      const sizeData = sizeMap.get(size);
      sizeData.revenue += revenue;
      sizeData.quantity += quantity;
      
      // Add to total revenue AFTER excluding items with empty or null size
      totalRevenue += revenue;
      
      if (!sizeData.orderIds.has(order.id)) {
        sizeData.orderCount++;
        sizeData.orderIds.add(order.id);
      }
    });
  });

  // Convert to array and calculate percentages
  const sizes = Array.from(sizeMap.values()).map(size => ({
    size: size.size,
    revenue: size.revenue,
    quantity: size.quantity,
    orderCount: size.orderCount,
    percentage: totalRevenue > 0 ? (size.revenue / totalRevenue) * 100 : 0
  }));

  // Sort by revenue descending and return top 10
  return sizes.sort((a, b) => b.revenue - a.revenue).slice(0, 10);
}

/**
 * Calculate Top Sizes by Style (breakdown of sizes within each style)
 * @param {Array} orders - Orders with order_items array
 * @returns {Array} Styles with their top sizes
 */
export function calculateTopSizesByStyle(orders) {
  const styleMap = new Map();

  orders.forEach(order => {
    const items = order.order_items || [];
    items.forEach(item => {
      // Skip items with empty or null size
      if (shouldSkipItem(item)) {
        return; // Skip this item
      }
      
      const styleNumber = item.style_number || 'Unknown';
      const attr2 = item.attr_2 || '';
      const fullStyle = `${styleNumber}${attr2}`.trim();
      const size = item.size || item.size_name || item.sizeName || item.Size || 'Unknown';
      
      // Use amount (line item total) to match order totals
      const revenue = parseFloat(item.amount || 0);
      const quantity = parseInt(item.quantity || item.qty || 0, 10);

      if (!styleMap.has(fullStyle)) {
        styleMap.set(fullStyle, {
          style: fullStyle,
          sizes: new Map()
        });
      }

      const styleData = styleMap.get(fullStyle);
      if (!styleData.sizes.has(size)) {
        styleData.sizes.set(size, {
          size: size,
          revenue: 0,
          quantity: 0,
          orderCount: 0,
          orderIds: new Set()
        });
      }

      const sizeData = styleData.sizes.get(size);
      sizeData.revenue += revenue;
      sizeData.quantity += quantity;
      if (!sizeData.orderIds.has(order.id)) {
        sizeData.orderCount++;
        sizeData.orderIds.add(order.id);
      }
    });
  });

  // Convert to nested array format
  const result = [];
  styleMap.forEach((styleData, styleName) => {
    const totalStyleRevenue = Array.from(styleData.sizes.values())
      .reduce((sum, size) => sum + size.revenue, 0);

    const sizes = Array.from(styleData.sizes.values())
      .map(size => ({
        size: size.size,
        revenue: size.revenue,
        quantity: size.quantity,
        orderCount: size.orderCount,
        percentage: totalStyleRevenue > 0 ? (size.revenue / totalStyleRevenue) * 100 : 0
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5); // Top 5 sizes per style

    if (sizes.length > 0) {
      result.push({
        style: styleName,
        totalRevenue: totalStyleRevenue,
        sizes: sizes
      });
    }
  });

  // Sort styles by total revenue and return top 10
  return result.sort((a, b) => b.totalRevenue - a.totalRevenue).slice(0, 10);
}


